import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlMeeting, GqlMeetingHeldKind, GqlMeetingKind } from '../../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { Card } from '@startuptools/ui/Card';
import { Typography } from '@startuptools/ui/Typography';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import {
  NextMeetingDocument,
  useCreateMeetingMutation,
  useDeleteMeetingMutation,
  useNextMeetingSuspenseQuery,
} from './NextMeeting.graphql';
import { CreateMeetingDialog } from '../../../common/components/meeting/CreateMeetingDialog';
import { Suspense, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { FormValues } from '../../../common/components/meeting/MeetingDetailsForm';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';

export const AiNextMeeting = ({ meeting }: { meeting: Pick<GqlMeeting, 'id'> }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();

  const [showCreateMeeting, setShowCreateMeeting] = useState(false);

  const {
    data: { meetings },
  } = useNextMeetingSuspenseQuery({ variables: { companyId } });
  const relevantMeeting = meetings.models.filter(m => m.createdByMeeting === meeting.id);
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateMeetingMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument, NextMeetingDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument, NextMeetingDocument],
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ startTime, endTime, ...values }) => {
    await doCreate({
      variables: {
        companyId,
        input: {
          startTime: startTime.toString(),
          endTime: endTime?.toString(),
          ...values,
          kind: GqlMeetingKind.BoardMeeting,
          usesStepper: true,
          createdByMeeting: meeting.id,
        },
      },
    });
    setShowCreateMeeting(false);
  };

  return (
    <>
      <Stack gap={1}>
        <Box>
          <Button variant="outlined" onClick={() => setShowCreateMeeting(true)}>
            <Trans id="Add Meeting" />
          </Button>
        </Box>
        {relevantMeeting.map(m => (
          <Card key={m.id}>
            <Stack direction="row" gap={1} justifyContent="space-between">
              <Typography>
                <Trans
                  id="Approve - #{number} <dateFormat>{date}</dateFormat>"
                  components={{
                    dateFormat: (
                      <DateFormat
                        locale={i18n.locale}
                        format={
                          m.heldKind === GqlMeetingHeldKind.PerCapsulam ? DateTime.DATE_SHORT : DateTime.DATETIME_SHORT
                        }
                      />
                    ),
                  }}
                  values={{ date: m.startTime, number: m.number }}
                />
              </Typography>
              <Box>
                <IconButton
                  color="danger"
                  onClick={() =>
                    doDelete({ variables: { companyId, input: { id: m.id, resetCreatorAgendaItem: true } } })
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>
      {showCreateMeeting && (
        <Suspense>
          <CreateMeetingDialog
            usesStepper
            onClose={() => setShowCreateMeeting(false)}
            meetingKind={GqlMeetingKind.BoardMeeting}
            onSubmit={onSubmit}
          />
        </Suspense>
      )}
    </>
  );
};
